<script setup>
import { reactive, ref, watch, onMounted } from 'vue'
import emailContentEditor from '../../composer/_emailContentEditor.vue'

const props = defineProps({
    node: Object,
})

const emit = defineEmits(['update:config'])

const templateId = ref(props.node?.data?.config?.template_id ?? null)
const emailContent = reactive({ subject: '', body: [] })

const templates = ref([])
const loadingList = ref(false)
const showPicker = ref(false)
const saveStatus = ref('')
const error = ref(null)
const sendingPreview = ref(false)

const showEditor = ref(false)

let subjectSaveTimer = null

watch(() => props.node, async (node) => {
    if (!node) return
    const newId = node.data?.config?.template_id ?? null
    if (newId !== templateId.value) {
        templateId.value = newId
        emailContent.subject = ''
        emailContent.body = []
        if (newId) await loadTemplate(newId)
    }
})

function setConfigTemplateId(id) {
    emit('update:config', { template_id: id ? Number(id) : null })
}

async function loadTemplate(id) {
    try {
        const response = await fetch(`/bo/communications/api/v1/workflows/email-templates/${id}`)
        if (!response.ok) throw new Error('Failed to load template')
        const result = await response.json()
        emailContent.subject = result.data.subject ?? ''
        emailContent.body = Array.isArray(result.data.body) ? result.data.body : []
    } catch (e) {
        error.value = 'Failed to load template'
    }
}

async function loadTemplateList() {
    loadingList.value = true
    try {
        const response = await fetch('/bo/communications/api/v1/workflows/email-templates')
        if (!response.ok) throw new Error('Failed to load templates')
        const result = await response.json()
        templates.value = result.data || []
    } catch (e) {
        error.value = 'Failed to load templates'
    } finally {
        loadingList.value = false
    }
}

function csrfHeaders() {
    return {
        'Content-Type': 'application/json',
        'X-CSRF-TOKEN': document.querySelector('meta[name="csrf-token"]')?.content ?? '',
        'Accept': 'application/json',
    }
}

async function saveSubject() {
    error.value = null
    saveStatus.value = 'Saving…'
    try {
        if (!templateId.value) {
            const response = await fetch('/bo/communications/api/v1/workflows/email-templates', {
                method: 'POST',
                headers: csrfHeaders(),
                body: JSON.stringify({ subject: emailContent.subject, body: emailContent.body }),
            })
            if (!response.ok) throw new Error('Failed to create template')
            const result = await response.json()
            templateId.value = result.data.id
            setConfigTemplateId(result.data.id)
        } else {
            const response = await fetch(`/bo/communications/api/v1/workflows/email-templates/${templateId.value}`, {
                method: 'PUT',
                headers: csrfHeaders(),
                body: JSON.stringify({ subject: emailContent.subject }),
            })
            if (!response.ok) throw new Error('Failed to save template')
        }
        saveStatus.value = 'Saved'
        setTimeout(() => { saveStatus.value = '' }, 1500)
    } catch (e) {
        error.value = 'Failed to save'
        saveStatus.value = ''
    }
}

function onSubjectInput() {
    clearTimeout(subjectSaveTimer)
    subjectSaveTimer = setTimeout(() => {
        subjectSaveTimer = null
        saveSubject()
    }, 600)
}

async function saveBody(newContent) {
    error.value = null
    saveStatus.value = 'Saving…'
    try {
        if (!templateId.value) {
            const response = await fetch('/bo/communications/api/v1/workflows/email-templates', {
                method: 'POST',
                headers: csrfHeaders(),
                body: JSON.stringify({ subject: emailContent.subject, body: newContent.body }),
            })
            if (!response.ok) throw new Error('Failed to create template')
            const result = await response.json()
            templateId.value = result.data.id
            emailContent.subject = result.data.subject
            emailContent.body = Array.isArray(result.data.body) ? result.data.body : []
            setConfigTemplateId(result.data.id)
        } else {
            const response = await fetch(`/bo/communications/api/v1/workflows/email-templates/${templateId.value}`, {
                method: 'PUT',
                headers: csrfHeaders(),
                body: JSON.stringify({ subject: newContent.subject, body: newContent.body }),
            })
            if (!response.ok) throw new Error('Failed to save template')
            emailContent.subject = newContent.subject
            emailContent.body = Array.isArray(newContent.body) ? newContent.body : []
        }
        saveStatus.value = 'Saved'
        setTimeout(() => { saveStatus.value = '' }, 1500)
    } catch (e) {
        error.value = 'Failed to save'
        saveStatus.value = ''
    }
}

function openPreview() {
    if (!templateId.value) return
    window.open(`/bo/communications/workflows/email-templates/${templateId.value}/preview`, '_blank', 'noopener')
}

async function sendPreview() {
    if (!templateId.value || sendingPreview.value) return
    error.value = null
    sendingPreview.value = true
    try {
        // Flush a pending subject edit so the inbox copy matches the panel.
        if (subjectSaveTimer) {
            clearTimeout(subjectSaveTimer)
            subjectSaveTimer = null
            await saveSubject()
        }
        const res = await axios.post(route('api.bo.crm.workflows.emailTemplates.sendPreview', templateId.value))
        saveStatus.value = `Sent to ${res.data.data.email}`
        setTimeout(() => { saveStatus.value = '' }, 3000)
    } catch (e) {
        error.value = e.response?.data?.message || 'Failed to send preview'
    } finally {
        sendingPreview.value = false
    }
}

async function togglePicker() {
    showPicker.value = !showPicker.value
    if (showPicker.value && templates.value.length === 0) {
        await loadTemplateList()
    }
}

async function pickExisting(id) {
    if (!id) return
    templateId.value = id
    setConfigTemplateId(id)
    await loadTemplate(id)
    showPicker.value = false
}

onMounted(async () => {
    if (templateId.value) {
        await loadTemplate(templateId.value)
    }
})
</script>

<template>
    <div class="config-panel__section">
        <label class="config-panel__label">Subject</label>
        <input
            v-model="emailContent.subject"
            class="config-panel__input"
            type="text"
            placeholder="Email subject"
            @input="onSubjectInput"
            @blur="saveSubject"
        />

        <div class="config-panel__row">
            <button type="button" class="config-panel__btn" @click="showEditor = true">
                Edit content
            </button>
            <button
                type="button"
                class="config-panel__btn config-panel__btn--secondary"
                :disabled="!templateId"
                :title="templateId ? 'Open the email in a new tab' : 'Save the email first'"
                @click="openPreview"
            >
                Preview
            </button>
            <button
                type="button"
                class="config-panel__btn config-panel__btn--secondary"
                :disabled="!templateId || sendingPreview"
                :title="templateId ? 'Send the email to your own inbox' : 'Save the email first'"
                @click="sendPreview"
            >
                {{ sendingPreview ? 'Sending…' : 'Send to me' }}
            </button>
        </div>
        <div v-if="saveStatus" class="text-center config-panel__status">{{ saveStatus }}</div>
        <div class="config-panel__picker">
            <button type="button" class="config-panel__link" @click="togglePicker">
                {{ showPicker ? 'Hide existing emails' : 'Use an existing email' }}
            </button>
            <select
                v-if="showPicker"
                class="config-panel__select"
                :disabled="loadingList"
                @change="pickExisting($event.target.value)"
            >
                <option value="">{{ loadingList ? 'Loading…' : '— Select an email —' }}</option>
                <option v-for="t in templates" :key="t.id" :value="t.id">
                    {{ t.subject || '(no subject)' }}
                </option>
            </select>
        </div>

        <p v-if="error" class="config-panel__error">{{ error }}</p>
        <p v-if="!templateId" class="config-panel__hint">A new email will be created on save.</p>
    </div>

    <modal-basic :open="showEditor" @closed="showEditor = false">
        <template v-slot:header>Edit Email Content</template>
        <template v-slot:body>
            <emailContentEditor
                v-if="showEditor"
                :presetEmailContent="emailContent"
                @update:emailContent="saveBody"
            />
            <div class="flex justify-end mt-4">
                <button type="button" class="myflow-basic-button" @click="showEditor = false">Done</button>
            </div>
        </template>
    </modal-basic>
</template>

<style scoped>
.config-panel__row {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-top: 8px;
}
.config-panel__btn {
    padding: 6px 12px;
    font-size: 12px;
    background: #2563eb;
    color: white;
    border: none;
    border-radius: 4px;
    cursor: pointer;
}
.config-panel__btn:disabled { opacity: 0.5; cursor: not-allowed; }
.config-panel__btn--secondary {
    background: white;
    color: #2563eb;
    border: 1px solid #2563eb;
}
.config-panel__status {
    font-size: 11px;
    color: #6b7280;
}
.config-panel__picker {
    margin-top: 12px;
    border-top: 1px solid #e5e7eb;
    padding-top: 10px;
}
.config-panel__link {
    background: none;
    border: none;
    color: #2563eb;
    font-size: 12px;
    padding: 0;
    cursor: pointer;
}
.config-panel__link:hover { text-decoration: underline; }
.config-panel__error {
    color: #dc2626;
    font-size: 12px;
    margin-top: 6px;
}
</style>
